
import { createSlice } from "@reduxjs/toolkit";

import {
  fetchMasterDatabaseRecords,
  fetchMasterDatabaseRecordById,
  createMasterDatabaseRecordThunk,
  importMasterDatabaseRecords,
  updateMasterDatabaseRecordThunk,
} from "./masterDatabaseThunk";

import type {
  MasterDatabaseRecord,
  MasterDatabasePagination,
  MasterDatabaseStats,
} from "../api/masterDatabase.api";

interface MasterDatabaseState {
  records: MasterDatabaseRecord[];
  pagination: MasterDatabasePagination | null;
  selectedRecord: MasterDatabaseRecord | null;
  stats: MasterDatabaseStats | null;

  loading: boolean;
  detailsLoading: boolean;
  creating: boolean;
  importing: boolean;
  updating: boolean;

  error: string | null;
  detailsError: string | null;
  createError: string | null;
  importError: string | null;
  updateError: string | null;

  createMessage: string | null;
  importMessage: string | null;
  updateMessage: string | null;
}

const initialState: MasterDatabaseState = {
  records: [],
  pagination: null,
  selectedRecord: null,
  stats: null,

  loading: false,
  detailsLoading: false,
  creating: false,
  importing: false,
  updating: false,

  error: null,
  detailsError: null,
  createError: null,
  importError: null,
  updateError: null,

  createMessage: null,
  importMessage: null,
  updateMessage: null,
};

const masterDatabaseSlice = createSlice({
  name: "masterDatabase",

  initialState,

  reducers: {
    clearMasterDatabaseError: (state) => {
      state.error = null;
    },

    clearMasterDatabaseDetailsError: (state) => {
      state.detailsError = null;
    },

    clearMasterDatabaseCreateError: (state) => {
      state.createError = null;
    },

    clearMasterDatabaseImportError: (state) => {
      state.importError = null;
    },

    clearMasterDatabaseUpdateError: (state) => {
      state.updateError = null;
    },

    clearMasterDatabaseMessages: (state) => {
      state.createMessage = null;
      state.importMessage = null;
      state.updateMessage = null;
    },

    clearSelectedMasterDatabaseRecord: (state) => {
      state.selectedRecord = null;
      state.detailsError = null;
    },

    clearMasterDatabase: () => initialState,
  },

  extraReducers: (builder) => {
    builder

      // Fetch paginated records and backend-calculated stats.
      .addCase(
        fetchMasterDatabaseRecords.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        },
      )

      .addCase(
        fetchMasterDatabaseRecords.fulfilled,
        (state, action) => {
          state.loading = false;
          state.records = action.payload.data ?? [];
          state.pagination = action.payload.pagination ?? null;
          state.stats = action.payload.stats ?? null;
        },
      )

      .addCase(
        fetchMasterDatabaseRecords.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.payload ||
            "Failed to fetch Master Database records";
        },
      )

      // Fetch a single record.
      .addCase(
        fetchMasterDatabaseRecordById.pending,
        (state) => {
          state.detailsLoading = true;
          state.detailsError = null;
        },
      )

      .addCase(
        fetchMasterDatabaseRecordById.fulfilled,
        (state, action) => {
          state.detailsLoading = false;
          state.selectedRecord = action.payload.data ?? null;
        },
      )

      .addCase(
        fetchMasterDatabaseRecordById.rejected,
        (state, action) => {
          state.detailsLoading = false;
          state.detailsError =
            action.payload ||
            "Failed to fetch Master Database record";
        },
      )

      // Create a manually entered record.
      .addCase(
        createMasterDatabaseRecordThunk.pending,
        (state) => {
          state.creating = true;
          state.createError = null;
          state.createMessage = null;
        },
      )

      .addCase(
        createMasterDatabaseRecordThunk.fulfilled,
        (state, action) => {
          state.creating = false;
          state.createMessage =
            action.payload.message ||
            "Master Database record created successfully.";

          // The page will refetch the list and stats after creation.
          // Avoid inserting here because search and pagination may be active.
        },
      )

      .addCase(
        createMasterDatabaseRecordThunk.rejected,
        (state, action) => {
          state.creating = false;
          state.createError =
            action.payload ||
            "Failed to create Master Database record";
        },
      )

      // Import Excel / CSV.
      .addCase(
        importMasterDatabaseRecords.pending,
        (state) => {
          state.importing = true;
          state.importError = null;
          state.importMessage = null;
        },
      )

      .addCase(
        importMasterDatabaseRecords.fulfilled,
        (state, action) => {
          state.importing = false;
          state.importMessage =
            action.payload.message ||
            `Successfully imported ${
              action.payload.data?.importedCount ?? 0
            } records`;
        },
      )

      .addCase(
        importMasterDatabaseRecords.rejected,
        (state, action) => {
          state.importing = false;
          state.importError =
            action.payload ||
            "Failed to import Master Database records";
        },
      )

      // Update an existing record.
      .addCase(
        updateMasterDatabaseRecordThunk.pending,
        (state) => {
          state.updating = true;
          state.updateError = null;
          state.updateMessage = null;
        },
      )

      .addCase(
        updateMasterDatabaseRecordThunk.fulfilled,
        (state, action) => {
          state.updating = false;

          const updatedRecord = action.payload.data;

          state.updateMessage =
            action.payload.message ||
            "Master Database record updated successfully";

          if (updatedRecord) {
            const recordIndex = state.records.findIndex(
              (record) => record.id === updatedRecord.id,
            );

            if (recordIndex !== -1) {
              state.records[recordIndex] = updatedRecord;
            }

            if (state.selectedRecord?.id === updatedRecord.id) {
              state.selectedRecord = updatedRecord;
            }
          }
        },
      )

      .addCase(
        updateMasterDatabaseRecordThunk.rejected,
        (state, action) => {
          state.updating = false;
          state.updateError =
            action.payload ||
            "Failed to update Master Database record";
        },
      );
  },
});

export const {
  clearMasterDatabaseError,
  clearMasterDatabaseDetailsError,
  clearMasterDatabaseCreateError,
  clearMasterDatabaseImportError,
  clearMasterDatabaseUpdateError,
  clearMasterDatabaseMessages,
  clearSelectedMasterDatabaseRecord,
  clearMasterDatabase,
} = masterDatabaseSlice.actions;

export default masterDatabaseSlice.reducer;
